import { createPopper } from '@popperjs/core'
import { isEmpty, isObject } from '../libs/helpTools'

const createClassElement = (className, tag = 'div') => {
  const block = document.createElement(tag)
  block.classList.add(className)
  return block
}

class Tooltip {
  constructor (el, options) {
    this.el = el
    this.delay = 0
    this.style = {}
    this.isPopover = false
    this.show = false
    this.hasArrow = true
    this.placement = null

    this.tooltip = null
    this.tooltipTitleElement = null
    this.tooltipDescriptionElement = null
    this.arrowElement = null

    this.tooltipTitle = null
    this.tooltipDescription = null

    this.popper = null
    this.isActive = false

    this.init(options)
  }

  static get showEvents () {
    return ['mouseenter', 'click']
  }

  static get hideEvents () {
    return ['mouseleave', 'focus']
  }

  init (options) {
    this.initTooltip()
    this.initPopper()
    this.updateOptions(options)
    if (!this.isPopover) this.addWatchers()
    else this.togglePopover(this.show, this)
  }

  initTooltip () {
    this.tooltip = createClassElement('ui-style-tooltip')
    this.tooltipTitleElement = createClassElement('ui-style-tooltip__title')
    this.tooltipDescriptionElement = createClassElement('ui-style-tooltip__description')

    this.tooltip.append(this.tooltipTitleElement, this.tooltipDescriptionElement)
  }

  setTooltipStyle (style, tooltip) {
    this.style = style || {}
    Object.entries(this.style).forEach(([key, value]) => {
      tooltip.style[key] = value
    })
  }

  initPopper () {
    this.popper = createPopper(this.el, this.tooltip, {
      modifiers: [
        { name: 'eventListeners', enabled: false },
        { name: 'offset', options: { offset: [0, 5] } }
      ]
    })
  }

  updateOptions ({
    isPopover = false,
    hasArrow = true,
    show = false,
    placement = 'top',
    delay = 0,
    title = '',
    description = '',
    style = {}
  }, tooltip = this) {
    this.isPopover = isPopover
    this.hasArrow = hasArrow
    this.show = show
    this.delay = delay
    this.setPlacement(placement)
    this.toggleArrow(hasArrow, tooltip)
    this.setTitle(title)
    this.setDescription(description)
    this.setTooltipStyle(style, tooltip.tooltip)
  }

  showTooltip = () => {
    if (isEmpty(this.tooltipTitle) && isEmpty(this.tooltipDescription)) {
      if (this.isActive) this.hideTooltip()
      return
    }

    this.isActive = true
    this.show = true
    setTimeout(() => {
      if (!this.isActive) return
      document.body.append(this.tooltip)
      requestAnimationFrame(() => this.tooltip.setAttribute('data-show', ''))
      this.enablePopperUpdate()
      this.popper.update()
    }, this.delay)
  }

  removeTooltipAfterHide = () => {
    if (this.tooltip.hasAttribute('data-show') && this.isActive) return
    this.tooltip.removeEventListener('transitionend', this.removeTooltipAfterHide)
    this.tooltip.remove()
  }

  hideTooltip = () => {
    this.isActive = false
    this.show = false
    this.tooltip.addEventListener('transitionend', this.removeTooltipAfterHide)
    this.tooltip.removeAttribute('data-show')
    this.disablePopperUpdate()
    this.popper.update()
  }

  changePopperUpdate (isEnabled) {
    this.popper.setOptions((options) => ({
      ...options,
      modifiers: [
        ...options.modifiers,
        { name: 'eventListeners', enabled: isEnabled }
      ]
    }))
  }

  enablePopperUpdate () {
    this.changePopperUpdate(true)
  }

  disablePopperUpdate () {
    this.changePopperUpdate(false)
  }

  addWatchers () {
    Tooltip.showEvents.forEach((event) => {
      this.el.addEventListener(event, this.showTooltip)
    })

    Tooltip.hideEvents.forEach((event) => {
      this.el.addEventListener(event, this.hideTooltip)
    })
  }

  removeWatchers () {
    Tooltip.showEvents.forEach((event) => {
      this.el.removeEventListener(event, this.showTooltip)
    })

    Tooltip.hideEvents.forEach((event) => {
      this.el.removeEventListener(event, this.hideTooltip)
    })
  }

  setTitle (text) {
    this.tooltipTitle = text
    this.tooltipTitleElement.textContent = this.tooltipTitle

    if (isEmpty(text)) {
      this.tooltipTitleElement.classList.add('hidden')
      return
    }
    this.tooltipTitleElement.classList.remove('hidden')
  }

  setDescription (text) {
    this.tooltipDescription = text
    if (isEmpty(text)) {
      this.tooltipDescriptionElement.classList.add('hidden')
      return
    }
    this.tooltipDescriptionElement.textContent = this.tooltipDescription
    this.tooltipDescriptionElement.classList.remove('hidden')
  }

  setPlacement (placement) {
    this.placement = placement
    this.popper.setOptions({ placement: this.placement })
  }

  togglePopover (show, tooltip) {
    if (show && !tooltip?.isActive) tooltip.showTooltip()
    else if (!show && tooltip?.isActive) tooltip.hideTooltip()
  }

  toggleArrow (hasArrow, tooltip = this) {
    if (hasArrow && tooltip.arrowElement) return
    if (!hasArrow) {
      tooltip.arrowElement?.remove()
      tooltip.arrowElement = null
      return
    }
    tooltip.arrowElement = document.createElement('div')
    tooltip.arrowElement.setAttribute('data-popper-arrow', '')
    tooltip.tooltip.append(this.arrowElement)
    tooltip.enablePopperUpdate()
  }

  destroy () {
    if (!this.isPopover) this.removeWatchers()
    this.tooltip.remove()
  }
}

export default {
  inserted (el, { value }) {
    if (!value) return
    let options = { delay: 200 }
    let title = ''
    let description = ''

    if (isObject(value)) {
      title = value.title ?? ''
      description = value.description ?? ''
      options = { ...options, ...value, title, description }
    } else {
      options = { ...options, description: value }
    }

    el.tooltip = new Tooltip(el, options)
  },
  update (el, { oldValue, value }) {
    if (isObject(value)) {
      el.tooltip.updateOptions(value, el.tooltip)
      if (el.tooltip?.isPopover) {
        el.tooltip.togglePopover(value.show, el.tooltip)
      }
    } else {
      if (oldValue !== value) el.tooltip?.setTitle(value)
    }
  },
  unbind (el) {
    el.tooltip?.destroy()
  }
}
